import { useSearchParams } from 'react-router'
import { useCallback, useState } from 'react'

export const useStateWithSearchParams = <T extends string | number>(
  key: string,
  defaultValue?: T,
  cacheKey?: string,
) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const value = searchParams.get(key)
  const [wasCleared, setWasCleared] = useState(false)

  const isNumber = typeof defaultValue === 'number'

  const setValue = useCallback((newValue?: T | null) => {
    if (newValue == undefined || newValue == 'undefined' || newValue?.toString() === 'NaN') {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev)
        next.delete(key)
        return next
      })
      if (cacheKey) {
        localStorage.removeItem(cacheKey)
      }
      setWasCleared(true)
    } else {
      const strValue = newValue.toString()
      setSearchParams(prev => {
        const next = new URLSearchParams(prev)
        next.set(key, strValue)
        return next
      })
      if (cacheKey) {
        localStorage.setItem(cacheKey, strValue)
      }
      setWasCleared(false)
    }
  }, [key, setSearchParams, cacheKey])

  const cachedValue = cacheKey ? localStorage.getItem(cacheKey) : null

  let finalValue: T | undefined
  if (value != null) {
    // Priority 1: searchParam
    finalValue = isNumber ? (parseInt(value, 10) as T) : (value as T)
  } else if (cacheKey && cachedValue != null) {
    // Priority 2: localStorage
    finalValue = isNumber ? (parseInt(cachedValue, 10) as T) : (cachedValue as T)
  } else if (!wasCleared) {
    // Priority 3: defaultValue (only if user hasn't explicitly cleared it)
    finalValue = defaultValue
  }

  return [
    finalValue as T,
    setValue,
  ] as const
}